import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useAuthStore } from '@/stores/auth'
import { scaricaBase64 } from '@/utils/download'
import { apiClient } from '@/api/client'

export type SseEventType =
  | 'ticket.nuovo'
  | 'ticket.risposta'
  | 'ticket.stato'
  | 'test.notifica'
  | 'avviso.sistema'
  | 'plugin.hook'
  | 'notifica.nuova'

export interface SsePayload {
  type: SseEventType
  entityId: number
  codice: string
  titolo: string
  messaggio: string
  url: string
  severity?: 'info' | 'warn' | 'danger'
  mandatory?: boolean
}

export interface PluginHookPayload {
  successo: boolean
  messaggio: string
  base64?: string
  filename?: string
  downloadUrl?: string
}

export interface InAppNotification {
  id: string
  type: SseEventType
  codice: string
  titolo: string
  messaggio: string
  url: string
  timestamp: Date
  read: boolean
  severity: 'info' | 'warn' | 'danger'
  mandatory: boolean
  /**
   * ID DB della notifica persistente (notifica.id).
   * Undefined per notifiche effimere (ticket.*, plugin.hook).
   * Presente per le notifiche in-app persistite su DB (type='notifica.nuova').
   */
  dbId?: number
}

// ─── Tipi locali per l'idratazione da API ────────────────────────────────────

interface NotificaDTO {
  id: number
  destinatarioId: number
  tipo: string | null
  titolo: string | null
  messaggio: string | null
  url: string | null
  severity: string | null
  mandatory: boolean
  letto: boolean
  dataLettura: string | null
  createdAt: string
}

interface NotificaPageDTO {
  content: NotificaDTO[]
  totalElements: number
  size: number
  number: number
}

const PREF_KEY = 'immigreer_notifiche_abilitate'

export const SSE_LABELS: Record<SseEventType, string> = {
  'ticket.nuovo':    'Nuovo ticket',
  'ticket.risposta': 'Nuova risposta',
  'ticket.stato':    'Ticket aggiornato',
  'test.notifica':   'Test notifica',
  'avviso.sistema':  'Comunicazione di sistema',
  'plugin.hook':     'Azione plugin',
  'notifica.nuova':  'Notifica',
}

// ─── Stato globale modulo — UN SOLO eventSource per sessione ─────────────────

const enabled = ref<boolean>(localStorage.getItem(PREF_KEY) !== 'false')
const notifications = ref<InAppNotification[]>([])
const unreadCount = computed(() => notifications.value.filter(n => !n.read).length)
const latestNotification = ref<InAppNotification | null>(null)
const mandatoryQueue = ref<InAppNotification[]>([])

// eventSource e reconnectTimer a livello modulo: condivisi tra tutti i consumer.
// Se fossero locali alla funzione ogni componente creerebbe la propria connessione
// SSE, causando notifiche duplicate (N componenti = N connessioni = N eventi).
let _eventSource: EventSource | null = null
let _reconnectTimer: ReturnType<typeof setTimeout> | null = null
let _consumerCount = 0  // ref-count: disconnect solo quando l'ultimo consumer smonta
// Inizializzato sul primo onMounted (sempre il layout component) — safe per useToast
let _toast: ReturnType<typeof useToast> | null = null

/**
 * Flag di idratazione: garantisce che l'API /notifiche venga interrogata una sola volta per
 * sessione. Viene resettato quando l'ultimo consumer smonta (logout / chiusura app),
 * in modo che il login successivo rilanci l'idratazione.
 */
let _hydrateFlag = false

// ─── Idratazione da API (notifiche persistenti) ───────────────────────────────

/**
 * Carica le ultime 20 notifiche persistite da backend e le fonde con quelle effimere
 * già in lista (ticket.*, plugin.hook). Le notifiche persistenti sopravvivono al reload
 * e vengono recapitate agli utenti offline (arrivano al login successivo).
 */
async function _hydrateFromApi(): Promise<void> {
  try {
    const { data } = await apiClient.get<NotificaPageDTO>('/api/v2/notifiche', {
      params: { unreadOnly: false, page: 0, size: 20 },
    })

    const persistent: InAppNotification[] = (data.content ?? []).map(n => ({
      id: String(n.id),
      dbId: n.id,
      type: 'notifica.nuova' as SseEventType,
      codice: n.tipo ?? '',
      titolo: n.titolo ?? '',
      messaggio: n.messaggio ?? '',
      url: n.url ?? '',
      timestamp: new Date(n.createdAt),
      read: n.letto,
      severity: (['info', 'warn', 'danger'].includes(n.severity ?? '')
        ? n.severity
        : 'info') as 'info' | 'warn' | 'danger',
      mandatory: n.mandatory ?? false,
    }))

    // Tieni le effimere (ticket.*, plugin.hook) già in lista; sostituisci le persistenti
    const ephemeral = notifications.value.filter(n => !n.dbId)
    notifications.value = [...persistent, ...ephemeral].slice(0, 30)
  } catch {
    // best-effort: silently fail (utente non autenticato o backend non raggiungibile)
  }
}

async function _hydrateIfNeeded(auth: ReturnType<typeof useAuthStore>): Promise<void> {
  if (_hydrateFlag || !auth.isAuthenticated) return
  _hydrateFlag = true
  await _hydrateFromApi()
}

// ─── Logica interna modulo ────────────────────────────────────────────────────

/**
 * Aggiunge una notifica effimera (ticket.*, avviso.sistema) alla lista.
 * Genera un id sintetico basato su type+entityId+timestamp.
 */
function pushNotification(payload: SsePayload): void {
  const notif: InAppNotification = {
    id: `${payload.type}-${payload.entityId}-${Date.now()}`,
    type: payload.type,
    codice: payload.codice,
    titolo: payload.titolo,
    messaggio: payload.messaggio,
    url: payload.url,
    timestamp: new Date(),
    read: false,
    severity: payload.severity ?? 'info',
    mandatory: payload.mandatory ?? false,
  }
  notifications.value = [notif, ...notifications.value].slice(0, 30)
  if (notif.mandatory) {
    mandatoryQueue.value = [...mandatoryQueue.value, notif]
  } else {
    latestNotification.value = notif
  }
}

/**
 * Aggiunge una notifica persistente arrivata via SSE (type='notifica.nuova').
 * Usa l'id reale della notifica DB come identificatore (payload.entityId = notifica.id).
 * Controlla i duplicati: se la notifica è già stata idratata dall'API, non la aggiunge di nuovo.
 */
function pushPersistentNotification(payload: SsePayload): void {
  const notifId = String(payload.entityId)
  if (notifications.value.some(n => n.id === notifId)) return

  const notif: InAppNotification = {
    id: notifId,
    dbId: payload.entityId,
    type: 'notifica.nuova',
    codice: payload.codice,
    titolo: payload.titolo,
    messaggio: payload.messaggio,
    url: payload.url,
    timestamp: new Date(),
    read: false,
    severity: payload.severity ?? 'info',
    mandatory: payload.mandatory ?? false,
  }
  notifications.value = [notif, ...notifications.value].slice(0, 30)
  if (notif.mandatory) {
    mandatoryQueue.value = [...mandatoryQueue.value, notif]
  } else {
    latestNotification.value = notif
  }
}

function _handlePluginHook(e: MessageEvent): void {
  try {
    const payload: PluginHookPayload = JSON.parse(e.data as string)
    if (_toast && payload.messaggio) {
      _toast.add({
        severity: payload.successo ? 'success' : 'warn',
        summary: payload.successo ? 'Completato' : 'Attenzione',
        detail: payload.messaggio,
        life: 4000,
      })
    }
    if (payload.successo) {
      if (payload.base64 && payload.filename) {
        scaricaBase64(payload.base64, payload.filename)
      } else if (payload.downloadUrl) {
        window.open(payload.downloadUrl, '_blank')
      }
    }
  } catch {
    // malformed data, ignora
  }
}

function _connect(auth: ReturnType<typeof useAuthStore>): void {
  if (typeof EventSource === 'undefined') return
  if (!enabled.value || !auth.isAuthenticated) return
  if (_eventSource) return  // già connesso — guard condiviso

  _eventSource = new EventSource('/api/v2/sse/events', { withCredentials: true })

  _eventSource.onopen = () => {
    if (_reconnectTimer) {
      clearTimeout(_reconnectTimer)
      _reconnectTimer = null
    }
  }

  // Handler per notifiche effimere (ticket.*, avviso.sistema, test.notifica)
  const ephemeralHandler = (e: MessageEvent) => {
    try {
      const payload: SsePayload = JSON.parse(e.data as string)
      if (enabled.value) pushNotification(payload)
    } catch {
      // malformed data, ignora
    }
  }

  // Handler per notifiche persistenti in-app
  const persistentHandler = (e: MessageEvent) => {
    try {
      const payload: SsePayload = JSON.parse(e.data as string)
      if (enabled.value) pushPersistentNotification(payload)
    } catch {
      // malformed data, ignora
    }
  }

  ;(['ticket.nuovo', 'ticket.risposta', 'ticket.stato', 'test.notifica', 'avviso.sistema'] as SseEventType[]).forEach(
    (type) => _eventSource!.addEventListener(type, ephemeralHandler)
  )
  _eventSource.addEventListener('notifica.nuova', persistentHandler)
  _eventSource.addEventListener('plugin.hook', _handlePluginHook)

  // Eventi ad-uso-componente (niente toast/campanella): il dispatch è centralizzato
  // qui così i subscriber sopravvivono alle riconnessioni dell'EventSource.
  for (const type of _COMPONENT_EVENT_TYPES) {
    _eventSource.addEventListener(type, (e: MessageEvent) => {
      try {
        const payload = JSON.parse(e.data as string)
        _componentListeners.get(type)?.forEach((h) => h(payload))
      } catch {
        // malformed data, ignora
      }
    })
  }

  _eventSource.onerror = () => {
    _disconnect()
    _reconnectTimer = setTimeout(() => _connect(auth), 5_000)
  }
}

function _disconnect(): void {
  if (_eventSource) {
    _eventSource.close()
    _eventSource = null
  }
}

// ─── Eventi SSE ad-uso-componente (es. note operative) ───────────────────────
// Non passano dal centro notifiche: il componente interessato si iscrive e
// reagisce (tipicamente ricaricando i propri dati).

const _COMPONENT_EVENT_TYPES = ['nota.aggiornata', 'nota.eliminata'] as const
type ComponentEventType = (typeof _COMPONENT_EVENT_TYPES)[number]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _componentListeners = new Map<string, Set<(payload: any) => void>>()

/** Iscrive un handler a un evento SSE di componente; ritorna l'unsubscribe. */
export function subscribeSse(
  type: ComponentEventType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handler: (payload: any) => void,
): () => void {
  let set = _componentListeners.get(type)
  if (!set) {
    set = new Set()
    _componentListeners.set(type, set)
  }
  set.add(handler)
  return () => {
    set.delete(handler)
  }
}

// ─── Composable pubblico ──────────────────────────────────────────────────────

export function useSseNotifications() {
  const auth = useAuthStore()

  function isSupported(): boolean {
    return typeof EventSource !== 'undefined'
  }

  function connect(): void { _connect(auth) }
  function disconnect(): void { _disconnect() }

  function toggle(): void {
    if (!isSupported()) return
    if (!enabled.value) {
      enabled.value = true
      localStorage.setItem(PREF_KEY, 'true')
      _connect(auth)
    } else {
      enabled.value = false
      localStorage.setItem(PREF_KEY, 'false')
      _disconnect()
      if (_reconnectTimer) {
        clearTimeout(_reconnectTimer)
        _reconnectTimer = null
      }
    }
  }

  /**
   * Marca tutte le notifiche come lette localmente e persiste via API
   * (per quelle persistenti su DB). Best-effort: gli errori API sono ignorati.
   */
  async function markAllRead(): Promise<void> {
    notifications.value = notifications.value.map(n => ({ ...n, read: true }))
    try {
      await apiClient.put('/api/v2/notifiche/lette-tutte')
    } catch {
      // best-effort
    }
  }

  /**
   * Marca una singola notifica come letta localmente.
   * Per le notifiche persistenti (dbId presente), persiste via API. Best-effort.
   */
  async function markRead(id: string): Promise<void> {
    const notif = notifications.value.find(n => n.id === id)
    notifications.value = notifications.value.map(n =>
      n.id === id ? { ...n, read: true } as InAppNotification : n
    )
    if (notif?.dbId !== undefined) {
      try {
        await apiClient.put(`/api/v2/notifiche/${notif.dbId}/letta`)
      } catch {
        // best-effort
      }
    }
  }

  function clearAll(): void {
    notifications.value = []
  }

  function confirmMandatory(id: string): void {
    mandatoryQueue.value = mandatoryQueue.value.filter(n => n.id !== id)
    void markRead(id)
  }

  onMounted(() => {
    _consumerCount++
    if (!_toast) _toast = useToast()
    // Idratazione: sempre al primo mount (se autenticato), indipendentemente da SSE enabled
    void _hydrateIfNeeded(auth)
    if (enabled.value) _connect(auth)
  })

  onUnmounted(() => {
    _consumerCount--
    // Disconnette solo quando l'ultimo consumer smonta (es. logout/chiusura app)
    if (_consumerCount <= 0) {
      _consumerCount = 0
      // Resetta il flag: al prossimo login l'idratazione ri-parte
      _hydrateFlag = false
      notifications.value = []
      _disconnect()
      if (_reconnectTimer) {
        clearTimeout(_reconnectTimer)
        _reconnectTimer = null
      }
    }
  })

  return {
    enabled,
    notifications,
    unreadCount,
    latestNotification,
    mandatoryQueue,
    isSupported,
    toggle,
    connect,
    disconnect,
    markAllRead,
    markRead,
    clearAll,
    confirmMandatory,
  }
}
